import * as React from 'react';
import { FloatingPortal } from '@floating-ui/react';
import { Transition, TransitionGroup, TransitionStatus } from 'react-transition-group';
import { hideToast, ToastPosition, ToastsStore, useToasts } from './toasts.store';
import { getGroupedToasts, TOAST_POSITIONS } from './utils';
import { useDidUpdate, useForceUpdate } from '../hooks';
import { ToastContainer } from './ToastContainer';
import { STATIC_STYLES, useContainerDynamicStyles } from './Toasts.styles';

export interface ToastsProps {
    /**
     * The default position for all toasts. This can be overridden when adding a toast.
     * @default bottom-end
     */
    position?: ToastPosition;

    /**
     * The default auto-close timeout for all toasts. This can be overridden when adding a toast.
     * If a number, the timeout timeout will be in milliseconds.
     * If `false`, there will be no auto-close timeout.
     * @default 4000
     */
    autoClose?: number | false;

    /**
     * The duration of the toast enter and exit transtitions, in milliseconds.
     * @default 250
     */
    transitionDuration?: number;

    /**
     * The max-width of a toast.
     * @default 440
     */
    toastMaxWidth?: number | string;

    /**
     * The max-height of a toast. This is used for transitions.
     * @default 200
     */
    toastMaxHeight?: number | string;

    /**
     * The width of the toast.
     * If `auto`, the toast will occupy the space defined by its content.
     * If `fill`, the toast will fill the toast container up to the defined max-width.
     * @default  fill
     */
    toastWidth?: 'fill' | 'auto';

    /**
     * The maximum number of toasts displayed at one time. Additonal toasts will be added to a queue.
     * @default 5
     */
    limit?: number;

    /**
     * A store for toasts state management. This can be used to create to create multiple toasts systems within an application.
     */
    store?: ToastsStore;

    /**
     * Whether the toasts are intended to be fixed to the browser viewport or bound within a particular parent element.
     * If `bound`, the toasts component needs to be a child of the intended parent element.
     * @default fixed
     */
    context?: 'fixed' | 'bound';
}

export function Toasts(props: ToastsProps): React.JSX.Element {
    const {
        position = 'bottom-end',
        autoClose = 4000,
        transitionDuration = 250,
        toastMaxWidth = 440,
        toastMaxHeight = 200,
        toastWidth = 'fill',
        limit = 5,
        store,
        context = 'fixed',
        ...other
    } = props;

    const data = useToasts(store);

    const forceUpdate = useForceUpdate();
    const groupElementRefs = React.useRef<Record<string, HTMLDivElement>>({});
    const previousLength = React.useRef<number>(0);

    const isBound = context === 'bound';

    React.useEffect(() => {
        store?.updateState((current) => ({
            ...current,
            limit: limit || 5,
            defaultPosition: position,
        }));
    }, [limit, position, store]);

    useDidUpdate(() => {
        if (data.toasts.length > previousLength.current) {
            setTimeout(() => forceUpdate(), 0);
        }
        previousLength.current = data.toasts.length;
    }, [data.toasts]);

    const grouped = getGroupedToasts(data.toasts, position);

    const cssVariables = useContainerDynamicStyles(
        toastMaxWidth,
        toastMaxHeight,
        toastWidth,
        transitionDuration,
        isBound ? 'absolute' : 'fixed'
    );

    const handleHideToast = (id: string) => hideToast(id, store);

    const groupedComponents = TOAST_POSITIONS.reduce((acc, pos) => {
        acc[pos] = grouped[pos].map(({ style: toastStyle, ...toast }) => {
            const toastId = toast.id as string;

            return (
                <Transition
                    key={toastId}
                    timeout={transitionDuration}
                    onEnter={() => groupElementRefs.current[toastId].offsetHeight} // force a reflow
                    nodeRef={{ current: groupElementRefs.current[toastId] }}
                >
                    {(state: TransitionStatus) => (
                        <ToastContainer
                            ref={(node) => {
                                if (node) {
                                    groupElementRefs.current[toastId] = node;
                                }
                            }}
                            data={toast}
                            onHide={handleHideToast}
                            autoClose={autoClose}
                            data-state={state}
                            data-position={pos}
                        />
                    )}
                </Transition>
            );
        });

        return acc;
    }, {} as Record<ToastPosition, React.ReactNode>);

    const groups = TOAST_POSITIONS.map((pos) => (
        <div key={pos} style={cssVariables} css={STATIC_STYLES} data-toast-group={true} data-position={pos} {...other}>
            <TransitionGroup data-transition-group={true}>{groupedComponents[pos]}</TransitionGroup>
        </div>
    ));

    if (context === 'bound') {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <>{groups}</>;
    }

    return <FloatingPortal>{groups}</FloatingPortal>;
}
